/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { makeStyles } from '@material-ui/core';
import React, { useCallback, useState } from 'react';

import {
  Alert,
  Button,
  LoadingSpinner,
  Select,
  SmartTable,
  TextField,
} from 'src/common/ui/components';
import TitleBar from 'src/components/pages/pageElements/titleBar';
import { UPDATE_PHARMACY_RECOMMENDATIONS } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { Staff_RecommendationType as RecommendationType } from 'src/generated/gql/graphql';
import {
  SelectPharmacyRecommendation,
  SelectPharmacyRecommendationReason,
  PharmacyRecommendationNote,
} from 'src/pharmacyRecommendations/components/SelectPharmacyRecommendation';
import { usePharmacyRecommendations } from 'src/pharmacyRecommendations/usePharmacyRecommendations';
import { STATES, STATES_BY_ABBREVIATION } from 'src/util/usStates';

const RecommendationTypeOptions = {
  ALL: 'All',
  [RecommendationType.NotRecommended]: 'Not Recommended',
  [RecommendationType.Recommended]: 'Recommended',
  [RecommendationType.Neutral]: 'Neutral',
};

const PharmacyRecommendations: React.VFC = () => {
  const classes = useStyles();

  const [cityInput, setCityInput] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState<string | null>(null);
  const [stateInput, setStateInput] = useState<string | null>(null);
  const [zipInput, setZipInput] = useState<string | null>(null);
  const [searchKey, setSearchKey] = useState<{
    city: string | null;
    name: string | null;
    recommendation: RecommendationType | null;
    state: string | null;
    zip: string | null;
  }>({
    city: null,
    name: null,
    recommendation: null,
    state: null,
    zip: null,
  });
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);

  const [recommendationTypeFilter, setRecommendationTypeFilter] =
    useState<RecommendationType | null>(null);

  const { paginatedPharmacies, error, mutate, loading } = usePharmacyRecommendations(
    searchKey.city,
    searchKey.name,
    searchKey.recommendation,
    searchKey.state,
    searchKey.zip,
    page,
    rowsPerPage,
  );

  const allowPharmacyUpdates = useFeatureFlag(UPDATE_PHARMACY_RECOMMENDATIONS);

  /**
   * Helper that allows us to update the pharmacies list in the swr cache without re-fetching
   * whenever a pharmacy recommendation is successfully updated.
   */
  const updatePharmacyList = useCallback(
    updatedPharmacy => {
      mutate(
        {
          staff_pharmacies: {
            ...paginatedPharmacies,
            pharmacies: paginatedPharmacies.pharmacies.map(pharmacy =>
              pharmacy.id === updatedPharmacy.id ? updatedPharmacy : pharmacy,
            ),
          },
        },
        false,
      );
    },
    [paginatedPharmacies, mutate],
  );

  if (error) {
    return <Alert title="Error fetching pharmacy recommendations" error={error} severity="error" />;
  }

  const setSearch = () => {
    // Reset to first page when search criteria changes
    setPage(1);
    setSearchKey({
      city: cityInput,
      name: nameInput,
      recommendation: recommendationTypeFilter,
      state: stateInput,
      zip: zipInput,
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <TitleBar title="Pharmacy Recommendations" />
      </div>

      <div className={classes.searchInput}>
        <TextField label="Pharmacy Name" onChange={e => setNameInput(e.target.value)} />
        <TextField label="City" onChange={e => setCityInput(e.target.value)} />
        <Select
          options={Object.values(STATES).map(state => state.abbreviation)}
          getOptionLabel={option => STATES_BY_ABBREVIATION[option]}
          onChange={(_, val: string | null) => setStateInput(val)}
          label="State"
        />
        <TextField label="Zip" onChange={e => setZipInput(e.target.value)} />
        <Select
          options={Object.keys(RecommendationTypeOptions)}
          getOptionLabel={option => {
            return RecommendationTypeOptions[option];
          }}
          onChange={(_, val: keyof typeof RecommendationTypeOptions | null) => {
            if (val === 'ALL') {
              setRecommendationTypeFilter(null);
            } else {
              setRecommendationTypeFilter(val);
            }
          }}
          label="Recommendation Filter"
          defaultValue="ALL"
        />
        <div className={classes.button}>
          <Button
            onClick={setSearch}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                setSearch();
              }
            }}
          >
            Search
          </Button>
        </div>
      </div>

      <div className={classes.table}>
        <SmartTable
          css={css`
            background: transparent;
            opacity: ${loading ? 0.25 : 1};

            /* Set widths for specific columns */
            td:nth-of-type(6),
            th:nth-of-type(6) {
              /* Recommendation column (6th column) */
              width: 15%;
            }

            td:nth-of-type(7),
            th:nth-of-type(7) {
              /* Recommendation Reasons column (7th column) */
              width: 20%;
            }

            td:nth-of-type(8),
            th:nth-of-type(8) {
              /* Recommendation Note column (8th column) */
              width: 20%;
            }
          `}
          columns={[
            { label: 'Name', key: 'name', sortable: false },
            { label: 'Street Address', key: 'street1', sortable: false },
            { label: 'City', key: 'city', sortable: false },
            { label: 'State', key: 'state', sortable: false },
            { label: 'Zip', key: 'zip', sortable: false },
            { label: 'Recommendation', key: 'recommendation', sortable: false },
            { label: 'Recommendation Reasons', key: 'recommendationReasons', sortable: false },
            { label: 'Note', key: 'recommendationNote', sortable: false },
          ]}
          controlled
          data={paginatedPharmacies.pharmacies.map(pharmacy => ({
            ...pharmacy,
            street1: `${pharmacy.street1}${pharmacy.street2 ? `, ${pharmacy.street2}` : ''}`,
            recommendation: (
              <SelectPharmacyRecommendation
                pharmacy={pharmacy}
                updatePharmacyList={updatePharmacyList}
                allowPharmacyUpdates={allowPharmacyUpdates}
              />
            ),
            recommendationReasons: (
              <SelectPharmacyRecommendationReason
                pharmacy={pharmacy}
                updatePharmacyList={updatePharmacyList}
                allowPharmacyUpdates={allowPharmacyUpdates}
              />
            ),
            recommendationNote: (
              <PharmacyRecommendationNote
                pharmacy={pharmacy}
                updatePharmacyList={updatePharmacyList}
                allowPharmacyUpdates={allowPharmacyUpdates}
              />
            ),
          }))}
          currentPage={page}
          rowsPerPage={rowsPerPage}
          totalRows={paginatedPharmacies.totalCount === 0 ? null : paginatedPharmacies.totalCount}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onRowsPerPageChange={newRowsPerPage => {
            setRowsPerPage(newRowsPerPage);
            setPage(1); // Reset to first page when changing rows per page
          }}
        />
        {loading ? (
          <figure
            css={css`
              display: block;
              margin: 0;
              padding: 0;
              position: relative;
            `}
          >
            <LoadingSpinner
              css={css`
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              `}
            />
          </figure>
        ) : null}
        {!loading && paginatedPharmacies.pharmacies.length === 0 && <div>No pharmacies found</div>}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    flex: '0 0 auto',
  },
  searchInput: {
    display: 'flex',
    flexDirection: 'column',
    width: 400,
    gap: theme.spacing(2),
  },
  button: {
    alignSelf: 'flex-end',
  },
  table: {
    marginTop: theme.spacing(2),
  },
}));

export default PharmacyRecommendations;
